import { Canvas } from "@react-three/fiber"
import { useCanvasStore } from "@state/canvasStore"
import styled from "styled-components"
import { handleContextLoss } from "./utils/context"
import { useLocation } from "react-router-dom"
import Compass from "./Compass"
import { Color, Vector3 } from "three"
import { useCallback, useEffect, useRef, useState } from "react"
import AnimateCamera from "@lib/AnimateCamera/AnimateCamera"
import { useStore } from "@state/store"
import { CAM_POSITION_INDEX } from "@data/cameraData"
import BuildingTouchPoints from "./BuildingTouchPoints"
import UnitLabel from "./UnitLabel"
import gsap from "gsap"
import { useGLTF } from "@common/hooks/useGLTF"
import { OrbitControls } from "@react-three/drei"
import UnitOverlayGeometry from "./UnitOverlayGeometry"
import { Gsc } from "vmi-gsc"

const intitalCameraSettings = {
  animationDuration: 2,
  position: CAM_POSITION_INDEX.start.position,
  target: CAM_POSITION_INDEX.start.target,
  fov: 60,
  near: 0.05,
  far: 10,
}

const initialControlsSettings = {
  rotateSpeed: 0.08,
  enableDamping: true,
  dampingFactor: 0.1,
  enableZoom: false,
  enablePan: false,
  enableRotate: true,
  enableKeys: false,
  // minPolarAngle: 0,
  maxPolarAngle: Math.PI * 0.5 - 0.2,
}

export const TCanvas = () => {
  const { pathname } = useLocation()
  const setCanvasReady = useCanvasStore((s) => s.setCanvasReady)

  const setCameraData = useCanvasStore((s) => s.setCameraData)
  const syncState = useStore((s) => s.syncState)
  const selectedUnit = useStore((s) => s.selectedUnit)
  const initialView = useRef(true)

  const splatDataOnLoad = () => {
    setTimeout(() => {
      setCameraData(CAM_POSITION_INDEX.wide)
    }, 1000)
    setCanvasReady(false)
  }

  const renderCanvasOn = ["/residences", "/orbits"]

  const filteredUnits = useStore((s) => s.filteredUnits)
  const controlsVisible = useStore((s) => s.controlsVisible)
  const residencePopup = useStore((s) => s.residencePopup)

  const handleClickMesh = (unit: any) => {
    if (selectedUnit?.[0]?.unitName === unit.unitName) {
      syncState({ selectedUnit: [], controlsVisible: true })
    } else {
      syncState({ selectedUnit: [unit], controlsVisible: false })
    }
  }
  const centerRef = useRef(CAM_POSITION_INDEX.corner.target)

  const onSelectedUnitChange = useCallback((unit) => {
    // syncState({ controlsVisible: false })
    centerRef.current = unit.center
  }, [])

  const [activeLabel, setActiveLabel] = useState<any>(null)

  useEffect(() => {
    // console.log(initialView.current)
    if (initialView.current) return
    const defaultCam = CAM_POSITION_INDEX.wide
    const unit = selectedUnit?.[0]

    if (unit) {
      centerRef.current = unit.center || centerRef.current

      setActiveLabel(unit)

      const dataPosition = unit.cameraPositionID
        .split(",")
        .map((pos) => parseFloat(pos))

      const indexedPosition = new Vector3(...dataPosition)

      setCameraData({
        position: indexedPosition || defaultCam.position,
        target: unit.center || centerRef.current,
      })
    } else {
      setActiveLabel(null)
      setCameraData(defaultCam)
    }
  }, [selectedUnit, centerRef.current])

  useEffect(() => {
    if (pathname !== "/residences") {
      setActiveLabel(null)
      setCameraData(CAM_POSITION_INDEX.wide)
      initialView.current = true
    } else {
      setCameraData(CAM_POSITION_INDEX.start)
      initialView.current = false
    }
  }, [pathname])

  const wrRef = useRef<any>(null)

  useEffect(() => {
    gsap.to(wrRef.current, {
      autoAlpha: renderCanvasOn.includes(pathname) ? 1 : 0,
      delay: renderCanvasOn.includes(pathname) ? 0.6 : 0,
    })
  }, [pathname])

  return (
    <CanvasWrapper ref={wrRef}>
      <Canvas
        dpr={1.27}
        frameloop="demand"
        onCreated={({ gl }) => {
          handleContextLoss(gl)
        }}
      >
        <mesh position-y={0.0} rotation-x={Math.PI * -0.5}>
          <planeGeometry args={[100, 100]} />
          <meshBasicMaterial toneMapped={false} color="#0E2620" />
          {/* <meshBasicMaterial toneMapped={false} color="#3a816f" /> */}
        </mesh>
        <group visible={renderCanvasOn.includes(pathname)}>
          {activeLabel && !residencePopup && (
            <UnitLabel center={centerRef.current} data={activeLabel} />
          )}
          {filteredUnits && (
            <>
              <BuildingTouchPoints />
              {/* <Test /> */}

              <UnitOverlayGeometry
                opacity={0.6}
                colours={{
                  on: new Color("#ACC7CD"),
                  off: new Color("#0A2620"),
                }}
                onSelectedUnitChange={onSelectedUnitChange}
                handleClickMesh={handleClickMesh}
                data={controlsVisible ? filteredUnits : filteredUnits}
                selectedUnit={selectedUnit?.[0]}
                glbSrc="/splats/newOverlays.glb"
              />
            </>
          )}

          <Gsc
            onLoaded={() => {
              splatDataOnLoad()
            }}
            src="/splats/trinity-splat-test-lowres.splat"
          />
        </group>
        <AnimateCamera
          initialControlsSettings={initialControlsSettings}
          intitalCameraSettings={intitalCameraSettings}
        />

        {/* <OrbitControls /> */}
      </Canvas>

      <Compass />
    </CanvasWrapper>
  )
}

const CanvasWrapper = styled.div`
  background-color: #0a2620;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
`

export default TCanvas
